import { useState, useEffect, useRef, useCallback } from "react"
import { Head, usePage, router } from "@inertiajs/react"
import { motion, AnimatePresence } from "motion/react"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import { SourceFavicon } from "@/components/source_favicon"
import {
  Check, Loader2, Circle, AlertCircle,
  Cloud, Terminal, ShoppingCart, Brain, Wallet, Heart, Leaf,
  Mic, Store, Building2, Layers, TrendingUp, GitBranch,
  Landmark, Briefcase, ArrowRight,
} from "lucide-react"

const ICON_MAP = {
  Cloud, Terminal, ShoppingCart, Brain, Wallet, Heart, Leaf,
  Mic, Store, Building2, Layers, TrendingUp, GitBranch,
  Landmark, Briefcase,
}

const TOPICS = [
  { slug: "saas", label: "SaaS", icon: "Cloud" },
  { slug: "devtools", label: "Dev Tools", icon: "Terminal" },
  { slug: "ecommerce", label: "E-Commerce & DTC", icon: "ShoppingCart" },
  { slug: "ai_ml", label: "AI & Machine Learning", icon: "Brain" },
  { slug: "fintech", label: "Fintech & Payments", icon: "Wallet" },
  { slug: "healthtech", label: "Health & Biotech", icon: "Heart" },
  { slug: "climate_tech", label: "Climate & Energy", icon: "Leaf" },
  { slug: "creator_economy", label: "Creator Economy", icon: "Mic" },
  { slug: "marketplaces", label: "Marketplaces & Platforms", icon: "Store" },
  { slug: "enterprise_sales", label: "Enterprise & B2B Sales", icon: "Building2" },
  { slug: "product_management", label: "Product & Design", icon: "Layers" },
  { slug: "growth_marketing", label: "Growth & Marketing", icon: "TrendingUp" },
  { slug: "open_source", label: "Open Source", icon: "GitBranch" },
  { slug: "venture_capital", label: "Venture Capital", icon: "Landmark" },
  { slug: "agency_consulting", label: "Agency & Consulting", icon: "Briefcase" },
]

const SETUP_STEPS = [
  { endpoint: "website", label: "Fetching your website..." },
  { endpoint: "summary", label: "Analyzing your brand..." },
  { endpoint: "posts", label: "Creating your first posts..." },
]

function SourceTicker({ sources }) {
  const tripled = [...sources, ...sources, ...sources]

  return (
    <div className="relative w-full overflow-hidden py-4">
      <div className="absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-background to-transparent" />
      <div className="absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-background to-transparent" />
      <motion.div
        className="flex gap-3 whitespace-nowrap"
        animate={{ x: ["0%", "-33.333%"] }}
        transition={{ duration: sources.length * 0.8, repeat: Infinity, ease: "linear", repeatType: "loop" }}
      >
        {tripled.map((source, i) => (
          <div
            key={`${source.name}-${i}`}
            className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-4 py-2 text-sm shadow-sm"
          >
            <SourceFavicon url={source.url} name={source.name} className="size-4" />
            <span className="text-foreground/80">{source.name}</span>
          </div>
        ))}
      </motion.div>
    </div>
  )
}

export default function Setup() {
  const { auth } = usePage().props
  const orgNanoId = auth.organization.nano_id
  const hasStartedSetup = useRef(false)

  const [phase, setPhase] = useState("topics") // "topics" | "setup"
  const [selectedTopics, setSelectedTopics] = useState([])
  const [matchedSources, setMatchedSources] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const [stepStates, setStepStates] = useState(() =>
    SETUP_STEPS.map(() => "pending")
  )

  const toggleTopic = (slug) => {
    setSelectedTopics((prev) =>
      prev.includes(slug) ? prev.filter((t) => t !== slug) : [...prev, slug]
    )
  }

  const handleContinue = useCallback(async () => {
    setSubmitting(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(`/${orgNanoId}/setup/topics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
          Accept: "application/json",
        },
        body: JSON.stringify({ topics: selectedTopics }),
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      setMatchedSources(data.sources || [])
      setPhase("setup")
    } catch (e) {
      console.error("Failed to save topics:", e)
    } finally {
      setSubmitting(false)
    }
  }, [orgNanoId, selectedTopics])

  const runSetupSteps = useCallback(async () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    for (let i = 0; i < SETUP_STEPS.length; i++) {
      setStepStates((prev) =>
        prev.map((s, idx) => (idx === i ? "active" : s))
      )

      try {
        const response = await fetch(
          `/${orgNanoId}/setup/${SETUP_STEPS[i].endpoint}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken || "",
              Accept: "application/json",
            },
          }
        )

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        setStepStates((prev) =>
          prev.map((s, idx) => (idx === i ? "complete" : s))
        )
      } catch {
        setStepStates((prev) =>
          prev.map((s, idx) => (idx === i ? "error" : s))
        )
      }
    }
  }, [orgNanoId])

  useEffect(() => {
    if (phase !== "setup" || hasStartedSetup.current) return
    hasStartedSetup.current = true
    runSetupSteps()
  }, [phase, runSetupSteps])

  useEffect(() => {
    const isComplete = stepStates.every((state) =>
      state === "complete" || state === "error"
    )
    if (!isComplete) return

    const timer = setTimeout(() => {
      router.visit(`/${orgNanoId}/setup/power_through`)
    }, 1000)
    return () => clearTimeout(timer)
  }, [orgNanoId, stepStates])

  const completedCount = stepStates.filter(
    (s) => s === "complete" || s === "error"
  ).length
  const progressValue = (completedCount / SETUP_STEPS.length) * 100

  return (
    <>
      <Head>
        <title>founder/mode | Setting up</title>
      </Head>

      <div className="flex min-h-svh flex-col items-center justify-center bg-background p-6">
        <AnimatePresence mode="wait">
          {phase === "topics" && (
            <motion.div
              key="topics"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -24, scale: 0.98, filter: "blur(4px)" }}
              transition={{
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
                exit: { duration: 0.45, ease: [0.4, 0, 1, 1] },
              }}
              className="w-full max-w-2xl"
            >
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2 text-center">
                  <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                    What do you want to post about?
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    This helps us curate your content sources.
                  </p>
                </div>

                <div className="flex flex-wrap justify-center gap-2">
                  {TOPICS.map((topic) => {
                    const Icon = ICON_MAP[topic.icon]
                    const isSelected = selectedTopics.includes(topic.slug)

                    return (
                      <motion.button
                        key={topic.slug}
                        onClick={() => toggleTopic(topic.slug)}
                        whileTap={{ scale: 0.96 }}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? "border-border bg-accent text-primary-foreground"
                            : "border-border/60 bg-card text-foreground/70 hover:border-border hover:bg-accent/50"
                        }`}
                      >
                        <AnimatePresence mode="wait" initial={false}>
                          {isSelected ? (
                            <motion.div
                              key="check"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                            >
                              <Check className="h-3.5 w-3.5 shrink-0 text-foreground/50" strokeWidth={2.5} />
                            </motion.div>
                          ) : (
                            <motion.div
                              key="icon"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                            >
                              {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {topic.label}
                      </motion.button>
                    )
                  })}
                </div>

                <div className="flex justify-center">
                  <Button
                    size="default"
                    disabled={selectedTopics.length === 0 || submitting}
                    onClick={handleContinue}
                  >
                    <span className="inline-flex items-center gap-1">
                      {submitting ? "Setting up..." : "Continue"}
                      {submitting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ArrowRight className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {phase === "setup" && (
            <motion.div
              key="setup"
              initial={{ opacity: 0, y: 24, scale: 0.98, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -24, scale: 0.98, filter: "blur(4px)" }}
              transition={{
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
                exit: { duration: 0.45, ease: [0.4, 0, 1, 1] },
              }}
              className="w-full max-w-lg flex flex-col gap-8"
            >
              <div className="flex flex-col gap-2 text-center">
                <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                  Building your feed
                </h1>
                <p className="text-sm text-muted-foreground">
                  {matchedSources.length} sources matched. Setting up your workspace
                </p>
              </div>

              {matchedSources.length > 0 && (
                <SourceTicker sources={matchedSources} />
              )}

              <div className="flex flex-col gap-1">
                {SETUP_STEPS.map((step, index) => (
                  <motion.div
                    key={step.endpoint}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.35,
                      delay: 0.15 + index * 0.1,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className={`flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors duration-300 ${
                      stepStates[index] === "active" ? "bg-secondary/60" : ""
                    }`}
                  >
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                      {stepStates[index] === "complete" && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                        </motion.div>
                      )}
                      {stepStates[index] === "active" && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                      {stepStates[index] === "pending" && (
                        <Circle className="h-2.5 w-2.5 text-muted-foreground/30" />
                      )}
                      {stepStates[index] === "error" && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.25 }}
                        >
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        </motion.div>
                      )}
                    </div>

                    <span
                      className={`text-sm transition-colors duration-300 ${
                        stepStates[index] === "active"
                          ? "text-foreground"
                          : stepStates[index] === "complete"
                            ? "text-muted-foreground"
                            : stepStates[index] === "error"
                              ? "text-destructive/80"
                              : "text-muted-foreground/30"
                      }`}
                    >
                      {step.label}
                    </span>
                  </motion.div>
                ))}
              </div>

              <Progress value={progressValue} className="h-1" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
